/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { verificationClient } from '../../../../services/verificationClient';
import { PolicyActionCategory } from '../../../../types/VerificationTypes';


/**
 * Read-only action categories registry.
 *
 * Mutations happen via Django seed migrations, not the UI.
 */
const ActionCategoriesSection: React.FC = () => {
  const [rows, setRows] = useState<PolicyActionCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await verificationClient.listActionCategories());
    } catch (err) {
      apiResponseHandler.handleApiError(err);
      setError('Could not load action categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-lg">
        <Loader2 className="animate-spin text-secondary-text-label" size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        variant="bordered"
        size="sm"
        icon="alert"
        title="Couldn’t load action categories"
        description={error}
        actions={<Button onClick={load}>Try again</Button>}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        variant="bordered"
        size="sm"
        icon="folder"
        title="No categories yet"
        description="Action categories are created via seed migrations."
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Code</TableHead>
          <TableHead>Sort</TableHead>
          <TableHead>Sensitive</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.code}>
            <TableCell>{r.name}</TableCell>
            <TableCell>
              <code className="text-xs">{r.code}</code>
            </TableCell>
            <TableCell>{r.sort_order}</TableCell>
            <TableCell>{r.is_sensitive ? <Tag variant="pending">yes</Tag> : '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ActionCategoriesSection;
